import { useState, useCallback, useMemo } from 'react';
import type { GanttData, Task, Dependency, DependencyType, Row } from '../types';
import { todayIso, addDays, diffDays, parseDate } from '../dateUtils';
import { genId } from '../idGen';
import { MIN_TASK_DURATION_DAYS } from '../constants';
import { autoArrange } from '../autoArrange';
import { autoSchedule } from '../autoSchedule';
import { getUnresolvedDependenciesMap } from './dependencies';
import { expandToSingleTaskRows } from '../expandToSingleTaskRows';
import { shiftUnresolvedTasks } from '../shiftTasks';
import { shiftFromUnresolvedDependencies } from '../operations/shiftFromUnresolvedDependencies';

export const EMPTY_GANTT_STATE: GanttData = { tasks: [], rows: [], dependencies: [] };

export type UseGanttStateReturn = {
  data: GanttData;
  setData: (data: GanttData) => void;

  addTask: (title: string) => Task;
  addTasksBulk: (titles: string[]) => void;

  updateTask: (id: string, patch: Partial<Omit<Task, 'id'>>) => void;
  moveTaskDates: (id: string, startDate: string, endDate: string) => void;

  removeTask: (id: string) => void;

  addDependency: (fromTaskId: string, toTaskId: string, type: DependencyType) => Dependency;
  removeDependency: (id: string) => void;

  createRow: () => Row;
  moveTaskToRow: (taskId: string, targetRowId: string, insertIndex?: number) => void;

  // New Row Actions
  addRowAbove: (rowId: string) => void;
  addRowBelow: (rowId: string) => void;
  splitRow: (rowId: string) => void;

  hasUnresolvedDepsMap: Map<string, boolean>;

  autoArrangeInPlace: () => void;
  autoScheduleInPlace: () => void;
  expandToSingleTaskRowsInPlace: () => void;
  shiftUnresolvedTasksInPlace: (days: number) => void;
  shiftFromUnresolvedDependenciesInPlace: () => void;
};

export const useGanttState = (initial: GanttData = EMPTY_GANTT_STATE): UseGanttStateReturn => {
  const [data, setData] = useState<GanttData>(initial);

  const addTask = useCallback((title: string): Task => {
    const startDate = todayIso();
    const endDate = addDays(startDate, 3);
    const task: Task = { id: genId('task'), title, startDate, endDate };
    const row: Row = { id: genId('row'), taskIds: [task.id] };
    setData(prev => ({ ...prev, tasks: [...prev.tasks, task], rows: [...prev.rows, row] }));
    return task;
  }, []);

  const addTasksBulk = useCallback((titles: string[]) => {
    const startDate = todayIso();
    const endDate = addDays(startDate, 3);

    const newTasks: Task[] = titles.map(title => ({
      id: genId('task'),
      title: title.trim() || 'Untitled',
      startDate,
      endDate,
    }));

    const newRows: Row[] = newTasks.map(task => ({
      id: genId('row'),
      taskIds: [task.id],
    }));

    setData(prev => ({
      ...prev,
      tasks: [...prev.tasks, ...newTasks],
      rows: [...prev.rows, ...newRows],
    }));
  }, []);

  const updateTask = useCallback((id: string, patch: Partial<Omit<Task, 'id'>>) => {
    setData(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => (t.id === id ? { ...t, ...patch } : t)),
    }));
  }, []);

  const moveTaskDates = useCallback((id: string, startDate: string, endDate: string) => {
    const d = diffDays(startDate, endDate);
    const safeEnd = d < MIN_TASK_DURATION_DAYS ? addDays(startDate, MIN_TASK_DURATION_DAYS) : endDate;

    setData(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => (t.id === id ? { ...t, startDate, endDate: safeEnd } : t)),
    }));
  }, []);

  const removeTask = useCallback((id: string) => {
    setData(prev => ({
      tasks: prev.tasks.filter(t => t.id !== id),
      rows: prev.rows
        .map(r => ({ ...r, taskIds: r.taskIds.filter(tid => tid !== id) }))
        .filter(r => r.taskIds.length > 0),
      dependencies: prev.dependencies.filter(d => d.fromTaskId !== id && d.toTaskId !== id),
    }));
  }, []);

  const addDependency = useCallback(
    (fromTaskId: string, toTaskId: string, type: DependencyType): Dependency => {
      const dep: Dependency = { id: genId('dep'), fromTaskId, toTaskId, type };
      setData(prev => ({ ...prev, dependencies: [...prev.dependencies, dep] }));
      return dep;
    },
    []
  );

  const removeDependency = useCallback((id: string) => {
    setData(prev => ({ ...prev, dependencies: prev.dependencies.filter(d => d.id !== id) }));
  }, []);

  const createRow = useCallback((): Row => {
    const row: Row = { id: genId('row'), taskIds: [] };
    setData(prev => ({ ...prev, rows: [...prev.rows, row] }));
    return row;
  }, []);

  const moveTaskToRow = useCallback(
    (taskId: string, targetRowId: string, insertIndex?: number) => {
      setData(prev => {
        const targetExists = prev.rows.some(r => r.id === targetRowId);
        if (!targetExists) return prev;

        const cleared = prev.rows.map(r => ({
          ...r,
          taskIds: r.taskIds.filter(tid => tid !== taskId),
        }));

        const updated = cleared.map(r => {
          if (r.id !== targetRowId) return r;
          const ids = [...r.taskIds];
          const idx = insertIndex === undefined ? ids.length : Math.max(0, Math.min(insertIndex, ids.length));
          ids.splice(idx, 0, taskId);
          return { ...r, taskIds: ids };
        });

        const normalized = updated.filter(r => r.taskIds.length > 0 || r.id === targetRowId);

        return { ...prev, rows: normalized };
      });
    },
    []
  );

  // --- New Row Actions ---

  const addRowAbove = useCallback((rowId: string) => {
    setData(prev => {
      const index = prev.rows.findIndex(r => r.id === rowId);
      if (index === -1) return prev;
      
      const newRow: Row = { id: genId('row'), taskIds: [] };
      const newRows = [...prev.rows];
      newRows.splice(index, 0, newRow);
      
      return { ...prev, rows: newRows };
    });
  }, []);

  const addRowBelow = useCallback((rowId: string) => {
    setData(prev => {
      const index = prev.rows.findIndex(r => r.id === rowId);
      if (index === -1) return prev;
      
      const newRow: Row = { id: genId('row'), taskIds: [] };
      const newRows = [...prev.rows];
      newRows.splice(index + 1, 0, newRow);
      
      return { ...prev, rows: newRows };
    });
  }, []);

  const splitRow = useCallback((rowId: string) => {
    setData(prev => {
      const index = prev.rows.findIndex(r => r.id === rowId);
      if (index === -1) return prev;

      const rowToSplit = prev.rows[index];
      if (rowToSplit.taskIds.length <= 1) return prev;

      // Create a map to quickly find tasks and sort them by start date
      const taskMap = new Map<string, Task>(prev.tasks.map(t => [t.id, t]));
      
      const tasksInRow = rowToSplit.taskIds
        .map(id => taskMap.get(id))
        .filter((t): t is Task => t !== undefined);

      // Sort tasks by start date
      tasksInRow.sort((a, b) => parseDate(a.startDate).getTime() - parseDate(b.startDate).getTime());

      // Create the new rows
      const splitRows: Row[] = tasksInRow.map((task, i) => ({
        id: i === 0 ? rowToSplit.id : genId('row'), // Keep original ID for the first row to preserve identity
        taskIds: [task.id]
      }));

      // Splice the old row out and put the new ones in
      const newRows = [...prev.rows];
      newRows.splice(index, 1, ...splitRows);

      return { ...prev, rows: newRows };
    });
  }, []);


  const hasUnresolvedDepsMap = useMemo(() => {
    return getUnresolvedDependenciesMap(data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.tasks, data.dependencies]); 

  const autoArrangeInPlace = useCallback(() => {
    setData(prev => autoArrange(prev));
  }, []);

  const autoScheduleInPlace = useCallback(() => {
    setData(prev => autoSchedule(prev));
  }, []);

  const expandToSingleTaskRowsInPlace = useCallback(() => {
    setData(prev => expandToSingleTaskRows(prev));
  }, []);

  

  const shiftUnresolvedTasksInPlace = useCallback((days: number) => {
    setData(prev => shiftUnresolvedTasks(prev, days));
  }, []);

  const shiftFromUnresolvedDependenciesInPlace = useCallback(() => {
    setData(prev => shiftFromUnresolvedDependencies(prev));
  }, []);

  return {
    data,
    setData,
    addTask,
    addTasksBulk,
    updateTask,
    moveTaskDates,
    removeTask,
    addDependency,
    removeDependency,
    createRow,
    moveTaskToRow,
    addRowAbove,
    addRowBelow,
    splitRow,
    hasUnresolvedDepsMap,
    
    autoArrangeInPlace,
    autoScheduleInPlace,
    expandToSingleTaskRowsInPlace,

    shiftUnresolvedTasksInPlace,
    shiftFromUnresolvedDependenciesInPlace,
  };
};
